import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { useAuth } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { localStorageService } from "../../services/LocalStorageService";

/** The Schedule Template layouts. Values double as their i18n label keys. */
export enum ScheduleTemplatePageTab {
	Weekly = "weekly.schedule.template",
	Consecutive = "consecutive.schedule.template",
	Coverage = "coverage.schedule.template",
}

const isScheduleTemplateTab = (value: string | null): value is ScheduleTemplatePageTab =>
	value === ScheduleTemplatePageTab.Weekly || value === ScheduleTemplatePageTab.Consecutive || value === ScheduleTemplatePageTab.Coverage;

/**
 * Owns the selected-tab state: persisted per-user in localStorage and gated by feature flags —
 * Consecutive by `ConsecutiveMasterSchedule`, Coverage by `CoveragePeopleTabs` (People has moved to the
 * Staffing area, so this flag now gates Coverage alone). If the persisted tab requires a flag that's off
 * it falls back to Weekly — both on initial load and if the flag flips off at runtime.
 */
export const useScheduleTemplateTab = () => {
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const isConsecutiveEnabled = useFeatureFlag(FEATURE_FLAGS.ConsecutiveMasterSchedule);
	const isCoverageEnabled = useFeatureFlag(FEATURE_FLAGS.CoveragePeopleTabs);

	const [selectedTab, setSelectedTabState] = useState<ScheduleTemplatePageTab>(() => {
		const persisted = userId ? localStorageService.getScheduleTemplateTab(userId) : null;
		if (!isScheduleTemplateTab(persisted)) return ScheduleTemplatePageTab.Weekly;
		if (persisted === ScheduleTemplatePageTab.Consecutive && isConsecutiveEnabled) return ScheduleTemplatePageTab.Consecutive;
		if (persisted === ScheduleTemplatePageTab.Coverage && isCoverageEnabled) return ScheduleTemplatePageTab.Coverage;
		return ScheduleTemplatePageTab.Weekly;
	});

	// Force back to weekly if the selected tab's flag becomes unavailable (turned off while selected).
	useEffect(() => {
		if (selectedTab === ScheduleTemplatePageTab.Consecutive && !isConsecutiveEnabled) setSelectedTabState(ScheduleTemplatePageTab.Weekly);
		if (selectedTab === ScheduleTemplatePageTab.Coverage && !isCoverageEnabled) setSelectedTabState(ScheduleTemplatePageTab.Weekly);
	}, [isConsecutiveEnabled, isCoverageEnabled, selectedTab]);

	const setSelectedTab = useCallback(
		(tab: ScheduleTemplatePageTab) => {
			setSelectedTabState(tab);
			if (userId) localStorageService.setScheduleTemplateTab(tab, userId);
		},
		[userId],
	);

	return { selectedTab, setSelectedTab, isConsecutiveEnabled, isCoverageEnabled };
};

type Props = {
	selectedTab: ScheduleTemplatePageTab;
	onChange: (tab: ScheduleTemplatePageTab) => void;
	isConsecutiveEnabled: boolean;
	isCoverageEnabled: boolean;
};

/**
 * Weekly / Consecutive / Coverage tab switcher injected into each Schedule Template view's header (as
 * its `trailing` slot). Weekly is always present; Consecutive appears with its flag; Coverage is gated
 * by `CoveragePeopleTabs` (People now lives in the Staffing area). Renders nothing when no extra layout
 * is enabled — there's only one then.
 */
export const ScheduleTemplateTabs: FC<Props> = ({ selectedTab, onChange, isConsecutiveEnabled, isCoverageEnabled }) => {
	const { t } = useTranslation();
	if (!isConsecutiveEnabled && !isCoverageEnabled) return null;

	const tabs: SystemTab<ScheduleTemplatePageTab>[] = [{ label: t(ScheduleTemplatePageTab.Weekly), value: ScheduleTemplatePageTab.Weekly }];
	if (isConsecutiveEnabled) tabs.push({ label: t(ScheduleTemplatePageTab.Consecutive), value: ScheduleTemplatePageTab.Consecutive });
	if (isCoverageEnabled) tabs.push({ label: t(ScheduleTemplatePageTab.Coverage), value: ScheduleTemplatePageTab.Coverage });

	return (
		<SystemTabs
			tabs={tabs}
			currentTab={selectedTab}
			onTabChange={onChange}
			dataTestId="schedule-template-tabs"
			data-annie-target="master-schedule-layout-tabs"
		/>
	);
};
